import { Constants } from "@socialcoach/services";
import axios from "./AxiosClient";
import { scCatchServiceException } from "../../annotations";
import { Page, CoachSocialNetworkProfile, SocialAccountOption } from "./dtos";

const BEDROCK = `${Constants.API_URL}/bedrock`;

export interface BulkAssignAssignment {
  playerId: number;
  selectedAccountId: string;
}

export interface BulkAssignRequest {
  assignments: BulkAssignAssignment[];
}

interface BulkAssignPartialFailureResponse {
  warning: string; // e.g., "Some assignments failed"
  messages: string[]; // List of specific error messages for failed assignments
}

export interface BulkAssignResponse {
  status: 204 | 207;
  data?: BulkAssignPartialFailureResponse;
}

export class CoachSocialNetworkProfileServices {
  @scCatchServiceException()
  static async listCoachProfiles(
    coachId: string | number,
    page: number = 1,
    perPage: number = 25
  ): Promise<Page<CoachSocialNetworkProfile>> {
    const { data } = await axios.get<Page<CoachSocialNetworkProfile>>(
      `${BEDROCK}/coaches/${coachId}/social_network_profiles`,
      {
        params: {
          page,
          perPage,
          extraFields: ["active_child_connections_count"],
        },
      }
    );
    return data;
  }

  @scCatchServiceException()
  static async showCoachProfile(coachId: string | number, id: string | number): Promise<CoachSocialNetworkProfile> {
    const { data } = await axios.get<CoachSocialNetworkProfile>(
      `${BEDROCK}/coaches/${coachId}/social_network_profiles/${id}`
    );
    return data;
  }

  @scCatchServiceException()
  static async deleteCoachProfile(coachId: string | number, id: string | number): Promise<void> {
    await axios.delete(`${BEDROCK}/coaches/${coachId}/social_network_profiles/${id}`);
  }

  @scCatchServiceException()
  static async getSelectableAccountOptions(
    coachId: string | number,
    id: string | number
  ): Promise<SocialAccountOption[]> {
    const { data } = await axios.get<SocialAccountOption[]>(
      `${BEDROCK}/coaches/${coachId}/social_network_profiles/${id}/account_options`
    );
    return data;
  }

  @scCatchServiceException()
  static async getActiveChildProfiles(
    coachId: string | number,
    id: string | number,
    page: number = 1,
    perPage: number = 25,
    extraFields: string[] = ["profile"]
  ): Promise<Page<CoachSocialNetworkProfile>> {
    const { data } = await axios.get<Page<CoachSocialNetworkProfile>>(
      `${BEDROCK}/coaches/${coachId}/social_network_profiles/${id}/children`,
      {
        params: { page, perPage, extraFields },
      }
    );
    return data;
  }

  @scCatchServiceException()
  static async bulkAssign(
    coachId: string | number,
    id: string | number,
    payload: BulkAssignRequest
  ): Promise<BulkAssignResponse> {
    const response = await axios.post<BulkAssignPartialFailureResponse | void>(
      `${BEDROCK}/coaches/${coachId}/social_network_profiles/${id}/bulk_assign`,
      payload
    );
    return {
      status: response.status as 204 | 207,
      data: response.status === 207 ? (response.data as BulkAssignPartialFailureResponse) : undefined,
    };
  }
}
